import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useExperiment, FeatureToggle } from '@printdeal/feature-management/react';
import {
  Badge, CheckboxPanel, DescriptionList, Grid, Icon, LinkAsButton,
  RadioCardGroup, RichText, Collapsible,
} from '@printdeal/ui-library';
import {
  hasDiscount,
  SupportArticleModal, useSelectorMachineContext, useSelectorMachineSend, VatModeContext,
} from '@printdeal/product-selector';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import cx from 'clsx';
import { Markdown } from '@printdeal/markdown/react';
import {
  useSegmentation, isCustomerProgramReseller,
} from '@printdeal/segmentation/react';
import { SERVICES } from '@printdeal/business-logic';
import { Money } from '../../../Money';
import { Service } from '../../../../types/product/productDetailPage';
import { DeliveryToolRadioCard } from '../DeliveryToolRadioCard';
import type { SelectedDeliveryTool } from '../../../../types/file-delivery/FileDelivery';

const FileChangeServiceCard = ({
  fileChangeService,
  handleDesignSelect,
  hasFileChangeService,
}: {
  fileChangeService: Service
  handleDesignSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  hasFileChangeService: boolean | undefined
}) => {
  const intl = useIntl();

  return (
    <Grid.Item>
      <DeliveryToolRadioCard
        title={fileChangeService.displayName || fileChangeService.name}
        price={fileChangeService.price}
        onChange={handleDesignSelect}
        active={Boolean(hasFileChangeService)}
        icon="photo-edit"
        value={fileChangeService.key}
        labelText={intl.formatMessage({ id: 'services.label.new', defaultMessage: 'New' })}
        supportArticleId={fileChangeService.readMore?.contentful_id}
      >
        {fileChangeService.description && (
        <RichText>
          <Markdown>{fileChangeService.description.description}</Markdown>
        </RichText>
        )}
      </DeliveryToolRadioCard>
    </Grid.Item>
  );
};

interface Props {
  services: Service[];
  selectedDeliveryTool?: SelectedDeliveryTool
  setSelectedDeliveryTool?: React.Dispatch<React.SetStateAction<SelectedDeliveryTool | undefined>>;
  selectedServices: string[];
  setIsChoiceSkipped?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedServices: React.Dispatch<React.SetStateAction<string[]>>;
}

export const hasServiceKey = (serviceKeys: string[], service: Service) =>
  serviceKeys.includes(service.key.toLowerCase());

const ServiceSelectionSection = ({
  services,
  selectedDeliveryTool,
  setSelectedDeliveryTool,
  selectedServices,
  setIsChoiceSkipped,
  setSelectedServices,
}: Props) => {
  const intl = useIntl();
  const { vatMode } = useContext(VatModeContext);
  const send = useSelectorMachineSend();
  const {
    experiment: exp1862Data,
    isInitialized: isExp1862Initialized,
  } = useExperiment('servicesprouts_exp1862_rerun_explicit_choice_filecheck');
  const segmentation = useSegmentation();
  const isReseller = isCustomerProgramReseller(
    segmentation.data?.isReseller,
    segmentation.data?.isPartOfCustomerProgram,
  );
  const [fileCheckSelection, setFileCheckSelection] = useState<String | undefined>(undefined);
  const useDefaultBehaviorExp1862 = !isExp1862Initialized || exp1862Data.variation !== 'variant' || isReseller;
  const useVariantBehaviorExp1862 = isExp1862Initialized && exp1862Data.variation === 'variant' && !isReseller;
  const [showSupportArticleModal, setShowSupportArticleModal] = useState(false);
  const merchantDeliveryTool = useSelectorMachineContext((state) => state.context.merchantRules.deliveryTool);
  const addDesignService = services.find((service) => service.name === SERVICES.FILE_DESIGN);
  const fileChangeService = services.find((service) => service.name === SERVICES.FILE_CHANGE);
  const fileCheckService = services.find((service) => service.name === SERVICES.FILE_CHECK);
  const hasAddDesignService = addDesignService && selectedServices.includes(addDesignService.key);
  const hasFileChangeService = fileChangeService && selectedServices.includes(fileChangeService.key);
  const hasFileCheckService = fileCheckService && selectedServices.includes(fileCheckService.key);
  const supportArticleId = fileCheckService?.readMore?.contentful_id;
  const [isFileServiceOpen, setIsFileServiceOpen] = useState(true);

  const serviceKeys = merchantDeliveryTool.servicekeys?.split(',') || [];
  const showFileCheckService = fileCheckService && hasServiceKey(serviceKeys, fileCheckService);
  const showAddDesignService = addDesignService && hasServiceKey(serviceKeys, addDesignService);
  const showFileChangeService = fileChangeService && hasServiceKey(serviceKeys, fileChangeService);
  const isServiceDiscounted = fileCheckService?.price?.price && fileCheckService?.price?.discountedPrice
    ? hasDiscount(vatMode, fileCheckService.price?.price, fileCheckService.price.discountedPrice) : false;

  useEffect(() => {
    send({
      type: 'Select service',
      selectedService: selectedServices.length > 0 ? selectedServices[0] : '',
    });
  }, [selectedServices, send]);

  const handleServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    setFileCheckSelection(value);
    if (setIsChoiceSkipped) {
      setIsChoiceSkipped(true);
    }
    setSelectedServices(checked
      ? [value]
      : selectedServices.filter((service) => service !== value));

    if (useVariantBehaviorExp1862) {
      setFileCheckSelection(value);
      if (value === 'no-service') {
        setSelectedServices(selectedServices.filter((service) => service !== fileCheckSelection));
      }
    }

    // Dispatch data layer event with the appropriate event action
    addDataLayerEntry('click_service_selection', {
      serviceKey: event.target.id,
      event: 'file_check_section',
      design_choice: checked && value !== 'no-service' ? 'manual_file_check' : 'automatic_file_check',
      eventNonInteraction: false,
    });
  };

  const filterService = useCallback((serviceKey: string) => {
    const [selectedService] = selectedServices.filter((service) => service !== serviceKey);

    if (selectedService) {
      setSelectedServices([selectedService]);
    } else {
      setSelectedServices([]);
    }
  }, [selectedServices, setSelectedServices]);

  const handleDesignSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleServiceChange(event);
    if (setSelectedDeliveryTool) {
      setSelectedDeliveryTool(undefined);
    }

    addDataLayerEntry('select_design_service', {
      selected: event.target.checked,
      title: event.target.id,
    });
  };

  useEffect(() => {
    if (selectedDeliveryTool && hasAddDesignService) {
      filterService(addDesignService.key);
    }

    if (selectedDeliveryTool && hasFileChangeService) {
      filterService(fileChangeService.key);
    }

    if (hasFileCheckService && hasAddDesignService) {
      filterService(fileCheckService.key);
    }

    if (hasFileCheckService && hasFileChangeService) {
      filterService(fileCheckService.key);
    }
  }, [selectedDeliveryTool,
    hasFileCheckService,
    hasAddDesignService,
    hasFileChangeService,
    filterService,
    addDesignService,
    fileCheckService,
    fileChangeService,
  ]);

  const toggleSupportArticleModal = (open: boolean) => {
    setShowSupportArticleModal(open);
    addDataLayerEntry(`support-article-${open ? 'open' : 'close'}`, { supportArticleId });

    addDataLayerEntry('file_check_section', {
      event: 'file_check_section',
      design_choice: 'manual_file_check_more_info',
      eventNonInteraction: false,
    });
  };

  const showFileCheckAsCheckbox = !hasAddDesignService
  && !hasFileChangeService
  && showFileCheckService
  && useDefaultBehaviorExp1862;

  return (
    <>
      <SupportArticleModal
        supportArticleId={supportArticleId}
        show={showSupportArticleModal}
        onClose={() => toggleSupportArticleModal(false)}
      />
      <Grid className="pd-grid-cols-2 pd-gap-4">
        {showFileChangeService && (
          <FileChangeServiceCard
            fileChangeService={fileChangeService}
            handleDesignSelect={handleDesignSelect}
            hasFileChangeService={hasFileChangeService}
          />
        )}
        {showAddDesignService && (
        <Grid.Item>
          <FeatureToggle id="file_design_service_pre-order">
            <FeatureToggle.Enabled>
              <DeliveryToolRadioCard
                title={addDesignService.displayName || addDesignService.name}
                price={addDesignService.price}
                onChange={handleDesignSelect}
                active={Boolean(hasAddDesignService)}
                icon="designer"
                value={addDesignService.key}
                labelText={intl.formatMessage({ id: 'services.label.new', defaultMessage: 'New' })}
                supportArticleId={addDesignService.readMore?.contentful_id}
              >
                {addDesignService.description && (
                <RichText>
                  <Markdown>{addDesignService.description.description}</Markdown>
                </RichText>
                )}
              </DeliveryToolRadioCard>
            </FeatureToggle.Enabled>
          </FeatureToggle>
        </Grid.Item>
        )}
      </Grid>
      {showFileCheckAsCheckbox && (
        <CheckboxPanel
          id={fileCheckService.key}
          name={fileCheckService.key}
          key={fileCheckService.key}
          checked={hasFileCheckService}
          onChange={handleServiceChange}
          value={fileCheckService.key}
        >
          <CheckboxPanel.Label className="pd-flex pd-items-center pd-gap-2">
            <span>
              {fileCheckService.displayName}
              {fileCheckService.price
                && fileCheckService.price?.price && fileCheckService.price.discountedPrice
                    && isServiceDiscounted && (
                    <span className="pd-pl-1">
                      (+
                      <span className={cx('pd-relative pd-inline-block pd-text-sm pd-text-dim-500 group-hover:pd-text-white pd-pl-1')}>
                        <span
                          className={cx(
                            'pd-absolute pd-left-0 pd-top-1/2 pd-block pd-w-12 pd-rotate-[-22deg] pd-border-1 pd-border-solid pd-border-error-500 group-hover:pd-border-white',
                          )}
                          aria-hidden="true"
                        />
                        <Money
                          value={fileCheckService.price.price[vatMode].centAmount}
                          currency={fileCheckService.price.price[vatMode].currencyCode}
                        />
                      </span>
                    </span>
              )}
              {fileCheckService.price && fileCheckService.price.discountedPrice && (
                <span className="pd-whitespace-nowrap pd-pl-1">
                  {isServiceDiscounted && (
                  <Money
                    value={fileCheckService.price.discountedPrice[vatMode].centAmount}
                    currency={fileCheckService.price.discountedPrice[vatMode].currencyCode}
                  />
                  )}
                  {!isServiceDiscounted && (
                  <>
                    <span className="pd-pr-1">(+</span>
                    <Money
                      value={fileCheckService.price.discountedPrice[vatMode].centAmount}
                      currency={fileCheckService.price.discountedPrice[vatMode].currencyCode}
                    />
                  </>
                  )}
                  )
                </span>
              )}
            </span>
            <Badge variant="success">
              <FormattedMessage id="services.file.check.badge.label" defaultMessage="For greater certainty." />
            </Badge>
          </CheckboxPanel.Label>
          <div className="pd-flex pd-gap-4">
            <DescriptionList className="pd-my-2 pd-flex">
              <DescriptionList.Content className="pd-text-dim-500">
                <FormattedMessage
                  id="services.label.description"
                  defaultMessage="Our specialists check for resolution, format, positioning, rotation, folding method, readability, cutouts and line widths and adjust your design where necessary."
                />
              </DescriptionList.Content>
            </DescriptionList>
            <Icon icon="discount-check" size="2xl" className="pd-text-success-500" />
          </div>
          <LinkAsButton
            className="pd-text-sm pd-font-normal"
            variant="secondary"
            onClick={() => toggleSupportArticleModal(true)}
            iconBefore="arrow-right"
            screenReaderLabel={intl.formatMessage({ id: 'service.filecheck.secondaryAction.screenReaderLabel', defaultMessage: 'More Information' })}
          >
            <FormattedMessage id="services.label.moreInfo" defaultMessage="More Info" />
          </LinkAsButton>
        </CheckboxPanel>
      )}
      {!hasAddDesignService && showFileCheckService && useVariantBehaviorExp1862 && (
        <Collapsible id="file-check-service-auto-file-check" isOpen={isFileServiceOpen} variant="secondary">
          <Collapsible.Header
            button={<Collapsible.Button onClick={() => setIsFileServiceOpen(!isFileServiceOpen)} />}
            title={(
              <FormattedMessage
                id="product.detail.fileCheckOptions.header.title"
                defaultMessage="Design Check"
              />
            )}
          >
            <FormattedMessage
              id="product.detail.fileCheckOptions.header.description"
              defaultMessage="Manual design check by a specialist"
            />
          </Collapsible.Header>
          <Collapsible.Panel>
            <div className="pd-flex pd-flex-col pd-gap-5">
              <RadioCardGroup
                id="alternative-upload-services"
                name="alternative-upload-services"
              >
                <RadioCardGroup.Fields className="pd-grid-cols-2">
                  <RadioCardGroup.Field
                    id="no-service"
                    value="no-service"
                    checked={!hasFileCheckService && fileCheckSelection === 'no-service'}
                    onChange={handleServiceChange}
                    label={<FormattedMessage id="product.detail.fileCheckOptions.automatic" defaultMessage="Automatic file check" />}
                    description={(
                      <>
                        <p className="pd-mb-2">
                          <FormattedMessage id="checkout.payment.fileCheck.automatic.free" defaultMessage="Free" />
                        </p>
                        <p>
                          <FormattedMessage
                            id="product.detail.fileCheckOptions.automatic.description"
                            defaultMessage="Our system checks your print-ready PDF-file automatically on a couple of relevant technological factors."
                          />
                        </p>
                      </>
                      )}
                    image={(
                      <Icon
                        icon="check"
                        size="2xl"
                        className={cx({
                          'pd-text-primary-500': !hasFileCheckService && fileCheckSelection === 'no-service',
                        })}
                      />
                      )}
                  />
                  <RadioCardGroup.Field
                    id={fileCheckService.key}
                    value={fileCheckService.key}
                    checked={hasFileCheckService}
                    onChange={handleServiceChange}
                    label={<FormattedMessage id="product.detail.fileCheckOptions.specialist" defaultMessage="Manual design check by a specialist" />}
                    description={(
                      <>
                        {fileCheckService.price && (
                          <p className="pd-mb-2 pd-whitespace-nowrap">
                            + <Money
                              value={fileCheckService.price[vatMode].centAmount}
                              currency={fileCheckService.price[vatMode].currencyCode}
                            />
                          </p>
                        )}
                        <p className="pd-mb-2">
                          <FormattedMessage
                            id="product.detail.fileCheckOptions.specialist.description"
                            defaultMessage="Our specialists check for resolution, size, positioning, rotation, folding, readability, cutouts and line thicknesses and adjust your design as needed."
                          />
                        </p>
                        <p className="pd-mb-2">
                          <LinkAsButton
                            className="pd-text-sm pd-font-normal"
                            variant="secondary"
                            onClick={() => toggleSupportArticleModal(true)}
                            iconBefore="arrow-right"
                            screenReaderLabel={intl.formatMessage({ id: 'service.filecheck.secondaryAction.screenReaderLabel', defaultMessage: 'More Information' })}
                          >
                            <FormattedMessage id="services.label.moreInfo" defaultMessage="More Info" />
                          </LinkAsButton>
                        </p>
                        <Badge variant="success">
                          <FormattedMessage id="product.detail.fileCheckOptions.specialist.badge.label" defaultMessage="For extra insurance" />
                        </Badge>
                      </>
                      )}
                    image={(
                      <Icon
                        icon="eye-check"
                        size="2xl"
                        className={cx({
                          'pd-text-primary-500': hasFileCheckService,
                        })}
                      />
                      )}
                  />
                </RadioCardGroup.Fields>
              </RadioCardGroup>
            </div>
          </Collapsible.Panel>
        </Collapsible>
      )}
    </>
  );
};

export default ServiceSelectionSection;
